import { isJsonString } from '@/utils/stringUtils';

import type {
  StructuredCard,
  StructuredPayload,
} from './structured-output/types';
import { Analysis, type AnalysisContent } from './structured-output/Analysis';
import { Clarify, type ClarifyContent } from './structured-output/Clarify';
import { CoachSelectionCard } from './structured-output/CoachSelectionCard';
import { type ConfirmContent } from './structured-output/Confirm';
import { GarminConnectCard } from './structured-output/GarminConnectCard';
import { General, type GeneralContent } from './structured-output/General';
import { LinkCard } from './structured-output/LinkCard';
import { Message } from 'ai';
import {
  Nutrition,
  type NutritionContent,
} from './structured-output/Nutrition';
import { PlanSelectionCard } from './structured-output/PlanSelectionCard';
import { PlanPreviewCard } from './structured-output/PlanPreviewCard';
import { StravaConnectCard } from './structured-output/StravaConnectCard';
import {
  WorkoutPlanner,
  type WorkoutPlannerContent,
} from './structured-output/WorkoutPlanner';

export const StructuredOutput = (props: {
  message: Message;
  onAction?: (action: string) => void;
}) => {
  const { onAction } = props;
  const messageId = props.message.id;
  let content = props.message.content;

  // Handle AI SDK content format (can be string, parts array, or single part object)
  const extractText = (val: any): string => {
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) return val.map(extractText).join('');
    if (val && typeof val === 'object') {
      if (val.type === 'text' && val.text) {
        return typeof val.text === 'string' ? val.text : val.text.value || '';
      }
      if ('text' in val) return String(val.text);
    }
    return '';
  };

  content = extractText(content);
  const isJson = isJsonString(content);
  const parsed = isJson ? (JSON.parse(content) as StructuredPayload) : content;

  const renderCard = (card: StructuredCard, idx: number, isClarify = false) => {
    if (card.type === 'strava_connect') {
      return (
        <div key={idx} className='w-full mt-2'>
          <StravaConnectCard />
        </div>
      );
    }

    if (card.type === 'garmin_connect') {
      return (
        <div key={idx} className='w-full mt-2'>
          <GarminConnectCard />
        </div>
      );
    }

    if (card.type === 'link' && typeof card.content === 'string') {
      return (
        <LinkCard
          key={idx}
          card={card as StructuredCard & { content: string; title?: string }}
          isClarify={isClarify}
        />
      );
    }

    if (card.type === 'plan_selection' && typeof card.content === 'string') {
      return (
        <PlanSelectionCard
          key={idx}
          card={card as StructuredCard & { content: string; title?: string }}
        />
      );
    }

    if (card.type === 'coach_selection') {
      return <CoachSelectionCard key={idx} />;
    }

    if (card.type === 'plan_preview' && typeof card.content === 'string') {
      return (
        <PlanPreviewCard
          key={idx}
          card={card as StructuredCard & { content: string }}
        />
      );
    }

    return null;
  };

  // Handle V3 Schema
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    'schema_version' in parsed &&
    (parsed.schema_version === '3.0' || parsed.schema_version === '3.0.0')
  ) {
    const { type, content, suggested_actions } = parsed;

    if (type === 'plan') {
      return <WorkoutPlanner content={content as WorkoutPlannerContent} />;
    }

    if (type === 'activity_analysis' || type === 'analysis') {
      return <Analysis content={content as AnalysisContent} />;
    }

    if (type === 'general') {
      const generalContent = content as GeneralContent;
      if (suggested_actions) {
        generalContent.suggested_actions = suggested_actions;
      }
      return (
        <General
          content={generalContent}
          renderCard={renderCard}
          onAction={onAction}
          messageId={messageId}
        />
      );
    }

    if (type === 'clarify') {
      const clarifyContent = content as ClarifyContent;
      if (suggested_actions && !clarifyContent.suggested_answers) {
        clarifyContent.suggested_answers = suggested_actions;
      }
      return (
        <Clarify
          content={clarifyContent}
          renderCard={renderCard}
          onAction={onAction}
          messageId={messageId}
        />
      );
    }

    if (type === 'nutrition') {
      return (
        <Nutrition
          content={content as NutritionContent}
          renderCard={renderCard}
        />
      );
    }

    if (type === 'confirm') {
      const confirmContent = content as ConfirmContent;
      const generalContent: GeneralContent = {
        text: confirmContent.question,
        intro: confirmContent.intro,
        cards: confirmContent.cards || [],
        suggested_actions: suggested_actions || ['Yes', 'No'],
      };
      return (
        <General
          content={generalContent}
          renderCard={renderCard}
          onAction={onAction}
          messageId={messageId}
        />
      );
    }

    // Fallback for V3 types not explicitly handled:
    // This catches V3 responses that might be a simple string or an error message.
    if (typeof content === 'string') return <div>{content}</div>;
    const contentObj = content as Record<string, any>;
    if (contentObj?.message)
      return <div className='text-red-500'>{contentObj.message}</div>;

    // If it's a V3 object but not of a known 'type', fall through to the generic object fallback below.
  }

  // Fallback for any content that wasn't a recognized V3 schema type,
  // or was a V3 object of unknown 'type'.
  if (typeof parsed === 'object' && parsed !== null) {
    return (
      <div className='whitespace-pre-wrap'>
        {JSON.stringify(parsed, null, 2)}
      </div>
    );
  }

  // Final fallback for primitive types (string, number, boolean)
  return <div>{parsed}</div>;
};
